import * as React from "react";

import type {
    ApolloFastlyQueryOptions,
    AsyncApolloFastlyQueryResult,
} from "@repo/data-gql-apollo";
import {documentNode, type GqlOperation} from "@repo/data-gql-core";

import ApolloGqlError from "./apollo-gql-error.ts";
import ApolloGqlClient from "./internal/apollo-gql-client.ts";
import ApolloQueryCore from "./internal/apollo-query-core.tsx";
import withApollo, {
    type WithApolloProps,
    type WithoutApollo,
} from "./with-apollo.tsx";

// @ts-expect-error - TS2344
type OwnProps<TData, TVariables> = ApolloFastlyQueryOptions<TVariables> & {
    // @ts-expect-error - TS2344 - Type 'TVariables' does not satisfy the constraint 'object'.
    query: GqlOperation<TData, TVariables>;
    children: (result: AsyncApolloFastlyQueryResult<TData>) => React.ReactNode;
};

type Props<TData, TVariables> = OwnProps<TData, TVariables> & WithApolloProps;

type DefaultProps<TData, TVariables> = {
    fastlyCaching: Props<TData, TVariables>["fastlyCaching"];
};

class ApolloFastlyQueryImpl<
    TData,
    TVariables extends Record<any, any>,
> extends React.Component<Props<TData, TVariables>> {
    // @ts-expect-error - TS2302 - Static members cannot reference class type parameters. | TS2302 - Static members cannot reference class type parameters.
    static defaultProps: DefaultProps<TData, TVariables> = {
        fastlyCaching: "PERSIST_UNTIL_PUBLISH",
    };

    constructor(props: Props<TData, TVariables>) {
        super(props);
    }

    _renderApolloQueryCore(): React.ReactNode {
        const {
            gqlClient,
            gqlDefaultModule,
            children,
            query,
            variables,
            fastlyCaching,
            module: localModule,
            ...remainingContext
        } = this.props;

        const coreClient = ApolloGqlClient.asApolloGqlClient(gqlClient);
        const context = ApolloGqlClient.getContext({
            module: localModule || gqlDefaultModule,
            ...remainingContext,
        });

        return (
            <ApolloQueryCore
                client={coreClient._getClient(context)}
                query={documentNode(query)}
                variables={variables}
                context={{
                    ...context,
                    fastlyCaching,
                }}
                /**
                 * The result of this query is Fastly cached. Refetching it
                 * wouldn't make sense unless Fastly cache has been flushed.
                 * (Client doesn't have any control over that.) We set the
                 * fetchPolicy as cache first so that we don't refetch once we
                 * get this query result.
                 */
                fetchPolicy="cache-first"
            >
                {({error, data, loading}) =>
                    children({
                        error:
                            error == null
                                ? undefined
                                : new ApolloGqlError(error),
                        data,
                        loading,
                    })
                }
            </ApolloQueryCore>
        );
    }

    render(): React.ReactNode {
        // If we are client-side, then we just use the regular query core to
        // handle all the things for us.
        return this._renderApolloQueryCore();
    }
}

// Wrap the component with the HOC that will inject GQL router things.
const ApolloFastlyQueryWithContext = withApollo(ApolloFastlyQueryImpl);

type ExportProps<TData, TVariables> = WithoutApollo<
    Props<TData, TVariables> & {
        fastlyCaching?: Props<TData, TVariables>["fastlyCaching"];
    }
>;

/**
 * Component to render a cached query.
 */
export default function <TData, TVariables extends Record<any, any>>(
    props: ExportProps<TData, TVariables>,
): React.ReactElement {
    // We know that the generic escapes here, but I can't work out a way to
    // avoid that when using an HOC around a genericly parameterized component.
    // We could likely define HOCs to accommodate this but then we'd lose the
    // handling of default props from React.ElementConfig. I'm not sure there's
    // a way to define a new `React.ElementConfig` that would work, so for now
    // we suppress the error :(
    // @ts-expect-error - TS2322 - Type '{ curriculum?: string | undefined; variables?: TVariables | undefined; module?: GAEModule | undefined; targetLocale?: string | undefined; query: GqlOperation<TData, TVariables>; fastlyCaching?: FastlyCaching | undefined; children: (result: AsyncApolloFastlyQueryResult<...>) => ReactNode; }' is not assignable to type 'Omit<Pick<ApolloFastlyQueryOptions<Record<any, any>> & { query: GqlOperation<unknown, Record<any, any>>; children: (result: AsyncApolloFastlyQueryResult<unknown>) => ReactNode; } & WithApolloProps, "curriculum" | ... 5 more ... | keyof WithApolloProps> & InexactPartial<...> & InexactPartial<...>, keyof WithApolloPro...'.
    return <ApolloFastlyQueryWithContext {...props} />;
}
